import { DEFAULT_RESUME_CONTENT, type ResumeContent, type ResumeSkillCategory } from './types';
import { normalizeConsent } from './consent';

const MAX_NORMALIZE_DEPTH = 8;

function normalizeTextValueInternal(
  value: unknown,
  depth: number,
  seen: WeakSet<object>
): string {
  if (value === null || value === undefined) return '';

  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);

  if (depth >= MAX_NORMALIZE_DEPTH) return '';

  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeTextValueInternal(item, depth + 1, seen).trim())
      .filter(Boolean)
      .join('\n');
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (seen.has(record)) return '';
    seen.add(record);

    const preferredTextKeys = [
      'html',
      'text',
      'content',
      'description',
      'summary',
      'responsibilities',
      'body',
      'value',
    ];

    for (const key of preferredTextKeys) {
      const candidate = record[key];
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate;
      }
    }

    const listKeys = ['content', 'items', 'children', 'lines', 'paragraphs', 'blocks', 'ops'];
    for (const key of listKeys) {
      const candidate = record[key];
      if (!Array.isArray(candidate)) continue;

      const parts = candidate
        .map((item) => {
          if (key === 'ops' && item && typeof item === 'object') {
            const insertValue = (item as Record<string, unknown>).insert;
            return normalizeTextValueInternal(insertValue, depth + 1, seen);
          }
          return normalizeTextValueInternal(item, depth + 1, seen);
        })
        .map((part) => part.trim())
        .filter(Boolean);

      if (parts.length > 0) {
        return parts.join('\n');
      }
    }

    const fallback = Object.values(record)
      .map((item) => normalizeTextValueInternal(item, depth + 1, seen).trim())
      .filter(Boolean)
      .join('\n');

    return fallback;
  }

  return '';
}

export function normalizeTextValue(value: unknown): string {
  return normalizeTextValueInternal(value, 0, new WeakSet<object>());
}

/**
 * Coerce one skill category into `{ id, name, skills }`.
 *
 * The AI extractors sometimes return `skills.categories` as a list of plain
 * strings. The builder store then stamps ids onto them with `{ ...item, id }`,
 * and spreading a string yields a character-indexed object
 * (`{ "0": "L", "1": "e", …, id }`) which is what gets persisted. Reading
 * `category.skills` off that is `undefined`, so every consumer that joins it —
 * the DOCX and TXT exporters — throws. Rebuild the name from those numeric keys
 * so an already-corrupted resume still renders and exports.
 */
function normalizeSkillCategory(value: unknown, index: number): ResumeSkillCategory | null {
  const fallbackId = `skill-category-${index}`;

  if (typeof value === 'string') {
    const name = value.trim();
    return name ? { id: fallbackId, name, skills: [] } : null;
  }

  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;

  const id = typeof record.id === 'string' && record.id ? record.id : fallbackId;
  const skills = Array.isArray(record.skills)
    ? record.skills.map((skill) => normalizeTextValue(skill)).filter(Boolean)
    : [];

  if (typeof record.name === 'string' && record.name.trim()) {
    return { id, name: record.name.trim(), skills };
  }

  const characters = Object.keys(record)
    .filter((key) => /^\d+$/.test(key))
    .sort((a, b) => Number(a) - Number(b))
    .map((key) => record[key]);

  const name = characters.every((char) => typeof char === 'string') ? characters.join('').trim() : '';

  return name ? { id, name, skills } : null;
}

/**
 * Field names the extraction model reaches for instead of the canonical ones.
 *
 * Autofill asks GPT-4o for JSON and casts the reply straight to `ResumeContent`
 * without validating it, and the prompt never spells the key names out — so the
 * model picks a plausible synonym and the field silently reads as empty. A
 * survey of stored resumes found `title` present on only 99 of 191 employment
 * entries, and `issuer` on 2 of 50 certifications.
 *
 * Resolving aliases on read repairs already-stored resumes with no migration.
 * The canonical name always wins; an alias is consulted only when it is absent
 * or blank.
 */
const FIELD_ALIASES = {
  employment: {
    title: ['title', 'jobTitle', 'position', 'role'],
    company: ['company', 'companyName', 'organization', 'organisation', 'employer'],
    responsibilities: ['responsibilities', 'description', 'details', 'summary', 'content'],
  },
  education: {
    institution: ['institution', 'school', 'university', 'college'],
    degree: ['degree', 'qualification'],
    field: ['field', 'fieldOfStudy', 'major', 'specialization', 'specialisation'],
    endDate: ['endDate', 'graduationDate', 'yearCompleted', 'completionDate'],
  },
  certifications: {
    name: ['name', 'title', 'certification'],
    issuer: ['issuer', 'issuingOrganization', 'institution', 'authority', 'organization'],
    date: ['date', 'issueDate', 'issuedDate', 'startDate'],
  },
} as const;

/** First alias holding real text, or `''`. */
function coalesceField(record: Record<string, unknown>, keys: readonly string[]): string {
  for (const key of keys) {
    const text = normalizeTextValue(record[key]);
    if (text.trim()) return text;
  }
  return '';
}

/** Rewrite one entry onto the canonical field names, leaving other keys alone. */
function canonicalizeEntry<T>(entry: T, aliases: Record<string, readonly string[]>): T {
  const record = entry as unknown as Record<string, unknown>;
  if (!record || typeof record !== 'object') return entry;

  const resolved: Record<string, unknown> = { ...record };
  for (const [canonical, keys] of Object.entries(aliases)) {
    resolved[canonical] = coalesceField(record, keys);
  }
  return resolved as T;
}

export function normalizeResumeContent(content: ResumeContent): ResumeContent {
  const safeContent: ResumeContent = {
    ...DEFAULT_RESUME_CONTENT,
    ...content,
    personalInfo: {
      ...DEFAULT_RESUME_CONTENT.personalInfo,
      ...(content.personalInfo ?? {}),
    },
    profile: {
      ...DEFAULT_RESUME_CONTENT.profile,
      ...(content.profile ?? {}),
    },
    contact: {
      ...DEFAULT_RESUME_CONTENT.contact,
      ...(content.contact ?? {}),
    },
    skills: {
      ...DEFAULT_RESUME_CONTENT.skills,
      ...(content.skills ?? {}),
      categories: Array.isArray(content.skills?.categories)
        ? content.skills.categories
            .map((category, index) => normalizeSkillCategory(category, index))
            .filter((category): category is ResumeSkillCategory => category !== null)
        : [],
      technical: Array.isArray(content.skills?.technical) ? content.skills.technical : [],
      soft: Array.isArray(content.skills?.soft) ? content.skills.soft : [],
    },
    links: Array.isArray(content.links) ? content.links : [],
    employment: Array.isArray(content.employment) ? content.employment : [],
    education: Array.isArray(content.education) ? content.education : [],
    certifications: Array.isArray(content.certifications) ? content.certifications : [],
    customSections: Array.isArray(content.customSections) ? content.customSections : [],
    // A boolean flag plus a plain string; kept out of `normalizeTextValue`,
    // which would flatten the record into its `text` value.
    consent: normalizeConsent(content.consent),
  };

  return {
    ...safeContent,
    profile: {
      ...safeContent.profile,
      headline: normalizeTextValue(safeContent.profile?.headline),
      summary: normalizeTextValue(safeContent.profile?.summary),
    },
    employment: (safeContent.employment ?? []).map((employment) =>
      canonicalizeEntry(employment, FIELD_ALIASES.employment)
    ),
    education: (safeContent.education ?? []).map((education) =>
      canonicalizeEntry(education, FIELD_ALIASES.education)
    ),
    certifications: (safeContent.certifications ?? []).map((certification) =>
      canonicalizeEntry(certification, FIELD_ALIASES.certifications)
    ),
    customSections: (safeContent.customSections ?? []).map((section) => ({
      ...section,
      content: normalizeTextValue(section.content),
    })),
  };
}
